import { Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from "@angular/core";
import { Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import _ from "lodash";
import { forkJoin, Observable, of, Subject, takeUntil } from "rxjs";
import { catchError } from "rxjs/operators";
import { DynamicFormComponent } from "src/app/core/dynamic-form/dynamic-form.component";
import {
  DynamicFormOptions,
  FieldConfig,
  FieldTypeEnum,
} from "src/app/core/dynamic-form/dynamicForm.interface";
import { EditHistoryData } from "src/app/core/edit-history/edit-history";
import { ToastService } from "src/app/core/toast/toast.service";
import { LocalStorageService } from "src/app/services/local-storage.service";
import { environment } from "src/environments/environment";
import { DynamicFormModule } from "src/app/core/dynamic-form/dynamic-form.module";
import { EditHistoryModule } from "src/app/core/edit-history/edit-history.module";
import { CommonModule } from "@angular/common";
import { QRCodeComponent } from "angularx-qrcode";
import { HttpClient } from "@angular/common/http";
import { customBreadCrumbRequest } from "src/app/core/bread-crumbs/bread-crumbs.component";
import { GestioneStepComponent } from "../../gestione-step/gestione-step.component";
import {
  MissioneEsplorativaLocationFields,
  MissionRole,
} from "../missioni-esplorative-dto";
import { MissioniEsplorativeService } from "../missioni-esplorative.service";
import { PoiService } from "../../poi/poi.service";
import { GestionePartiteService } from "../../gestione-partite/gestione-partite.service";
import { MappaIndoorComponent } from "../../mappa-indoor/mappa-indoor.component";
import { MappaIndoorService } from "../../mappa-indoor/mappa-indoor.service";
import {
  CreateMission,
  LevelUnlockConfig,
  MissionDto,
  UNLOCK_TYPE_LABEL,
  UnlockType,
  UpdateMission,
} from "../../gestione-missioni/missioni-dto";
import { GenericDD } from "src/app/model/generic-data";

interface RoleOption extends GenericDD {
  id: string;
  codice: string;
}

interface RoleApiResponse {
  id: string;
  name: string;
  description?: string;
}

interface WeekDayOption {
  value: string;
  label: string;
}

@Component({
  selector: "app-form-missioni-esplorative",
  imports: [
    CommonModule,
    DynamicFormModule,
    EditHistoryModule,
    GestioneStepComponent,
    MappaIndoorComponent,
    QRCodeComponent,
  ],
  templateUrl: "./form-missioni-esplorative.component.html",
  styleUrl: "./form-missioni-esplorative.component.scss",
})
export class FormMissioniEsplorativeComponent implements OnInit, OnDestroy {
  @ViewChild("myForm") myForm!: DynamicFormComponent;
  @ViewChild(MappaIndoorComponent) mappaIndoor?: MappaIndoorComponent;
  @ViewChildren("qrComp", { read: ElementRef }) qrComps!: QueryList<ElementRef>;

  // flag: la missione e una mappa indoor
  isIndoor = false;

  public object?: MissionDto & MissioneEsplorativaLocationFields;
  loadingQrCode = false;

  codice: string = "";

  form2Opts: DynamicFormOptions;

  ready: boolean = false;
  editHistoryData: EditHistoryData | undefined;

  regConfig2: FieldConfig[] = [];
  poiOptions: GenericDD[] = [];
  adventureOptions: GenericDD[] = [];
  roleOptions: RoleOption[] = [];
  defaultRoleId: string | null = null;
  unlockTypeOptions: GenericDD[] = Object.values(UnlockType).map((unlockType) => ({
    id: unlockType,
    codice: UNLOCK_TYPE_LABEL[unlockType],
  }));
  weekDayOptions: WeekDayOption[] = [
    { value: "sunday", label: "Dom" },
    { value: "monday", label: "Lun" },
    { value: "tuesday", label: "Mar" },
    { value: "wednesday", label: "Mer" },
    { value: "thursday", label: "Gio" },
    { value: "friday", label: "Ven" },
    { value: "saturday", label: "Sab" },
  ];

  private theEnd$ = new Subject<void>();
  private readonly timeScheduleFieldNames = ["startAt", "endAt"];
  private readonly timeScheduleDayPrefixes = ["startDay"];
  private readonly endDayOrder = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];

  constructor(
    private http: HttpClient,
    private route: ActivatedRoute,
    private router: Router,
    private toast: ToastService,
    private missioniEsplorativeService: MissioniEsplorativeService,
    private poiService: PoiService,
    private gameService: GestionePartiteService,
    private mappaIndoorService: MappaIndoorService,
    private localStorageService: LocalStorageService,
  ) {
    this.form2Opts = {
      buttonAtBottom: true,
      formType: "add",
      editOptions: {
        entityName: "explorative-mission",
        entityDisplayName: "Missione esplorativa",
        deleteUrl: `${environment.apiUrl}/missions`,
        deleteMessage: `Stai per eliminare la missione esplorativa, procedere?`,
        deleteItem: "",
        entityId: "",
        isFem: false,
        redirectUrl: `home/missioni-esplorative`,
      },
      addOptions: {
        entityName: "explorative-mission",
        entityDisplayName: "Missione esplorativa",
        isFem: false,
        redirectUrl: `home/missioni-esplorative`,
      },
    };
  }

  ngOnDestroy(): void {
    this.theEnd$.next();
    this.theEnd$.complete();
  }

  ngOnInit(): void {
    // Carica POI, avventure e ruoli in un colpo solo e costruisce il form UNA volta:
    // evita ricostruzioni multiple del form (che lasciavano nodi DOM fantasma per via
    // delle animazioni di re-render quando i loader risolvevano in tempi diversi).
    this.loadOptions();
  }

  initializeForm() {
    if (this.router.url.includes("new")) {
      this.form2Opts.formType = "add";
      this.localStorageService.setTitolo("Nuova missione esplorativa");
      this.initializeFormFields();
    } else {
      this.form2Opts.formType = "edit";
      this.form2Opts.buttonAtBottom = false;
      this.initializeEditForm();
    }
  }

  initializeFormFields() {
    console.log("[MissioniEsplorative] object before form fields:", this.object);

    this.regConfig2 = [
      {
        type: FieldTypeEnum.input,
        name: "name",
        label: "Nome",
        isRequired: true,
        width: "w-full",
        value: this.object ? this.object.name : undefined,
        validations: [
          {
            name: "required",
            validator: Validators.required,
            message: "Inserisci un nome",
          },
        ],
      },
      {
        type: FieldTypeEnum.textarea,
        name: "shortDescription",
        label: "Breve descrizione",
        isRequired: true,
        width: "w-full",
        value: this.object ? this.object.shortDescription : undefined,
        validations: [
          {
            name: "required",
            validator: Validators.required,
            message: "Inserisci una breve descrizione",
          },
        ],
      },
      {
        type: FieldTypeEnum.textarea,
        name: "description",
        label: "Descrizione",
        isRequired: true,
        width: "w-full",
        value: this.object ? this.object.description : undefined,
        validations: [
          {
            name: "required",
            validator: Validators.required,
            message: "Inserisci una descrizione",
          },
        ],
      },
      {
        type: FieldTypeEnum.numberInput,
        name: "pointsRedeem",
        label: "Punti",
        isRequired: true,
        width: "w-full",
        value: this.object && this.object.pointsRedeem ? this.object.pointsRedeem : 0,
        validations: [
          {
            name: "required",
            validator: Validators.required,
            message: "Inserisci un valore",
          },
        ],
      },
      {
        type: FieldTypeEnum.numberInput,
        name: "hearts",
        label: "Cuori",
        isRequired: true,
        width: "w-full",
        value: this.object ? this.object.hearts : 1,
        validations: [
          {
            name: "required",
            validator: Validators.required,
            message: "Inserisci il numero di cuori",
          },
          {
            name: "min",
            validator: Validators.min(1),
            message: "E richiesto almeno un cuore",
          },
        ],
      },
      {
        type: FieldTypeEnum.autoComplete,
        name: "missionCategoryFk",
        label: "Categoria missione",
        isRequired: false,
        width: "w-full",
        value: this.object?.missionCategory?.name ?? this.object?.missionCategoryName ?? null,
        placeholder: "Seleziona una categoria",
        typeAheadOptionField: "codice",
        options: {
          api: true,
          url: "-missions/categories",
        },
      },
      {
        type: FieldTypeEnum.select,
        name: "missionRoles",
        label: "Ruolo",
        isRequired: true,
        width: "w-full",
        value: this.getSelectedRoleValue(),
        placeholder: "Seleziona uno o piu ruoli",
        cssClass: "rolefk-select",
        data: this.roleOptions,
        options: {
          api: false,
          multiple: true,
        },
      },
      {
        type: FieldTypeEnum.select,
        name: "adventureFk",
        label: "Avventura",
        isRequired: false,
        width: "w-full",
        value:
          this.object?.adventureFk ??
          this.route.snapshot.paramMap.get("adventureId") ??
          null,
        data: this.adventureOptions,
        showEmptyOption: true,
      },
      {
        type: FieldTypeEnum.select,
        name: "poiFk",
        label: "POI collegato",
        isRequired: false,
        width: "w-full",
        value: this.object?.poiFk ?? null,
        data: this.poiOptions,
        showEmptyOption: true,
      },
      {
        type: FieldTypeEnum.switch,
        name: "isIndoor",
        label: "E una mappa indoor?",
        isRequired: false,
        width: "w-full",
        value: this.isIndoor,
      },
      {
        type: FieldTypeEnum.select,
        name: "unlockType",
        label: "Tipo sblocco",
        isRequired: true,
        width: "w-full",
        value: this.object?.unlockType ?? UnlockType.immediate,
        data: this.unlockTypeOptions,
        validations: [
          {
            name: "required",
            validator: Validators.required,
            message: "Seleziona un tipo sblocco",
          },
        ],
      },
      {
        type: FieldTypeEnum.numberInput,
        name: "requiredLevel",
        label: "Livello richiesto di sblocco",
        isRequired: true,
        width: "w-full",
        value: this.getRequiredLevelValue(),
        hidden: this.object?.unlockType !== UnlockType.level,
        options: {
          inputMinValue: "1",
        },
      },
      {
        type: FieldTypeEnum.divider,
        name: "timeScheduleDivider",
        width: "w-full",
        hidden: this.object?.unlockType !== UnlockType.time,
      },
      ...this.buildDayCheckboxFields("startDay", "Giorni inizio"),
      {
        type: FieldTypeEnum.input,
        name: "startAt",
        label: "Orario inizio",
        inputType: "time",
        isRequired: true,
        width: "w-1/2",
        value: this.getTimeScheduleTimeValue("startAt"),
        hidden: this.object?.unlockType !== UnlockType.time,
      },
      {
        type: FieldTypeEnum.input,
        name: "endAt",
        label: "Orario fine",
        inputType: "time",
        isRequired: true,
        width: "w-1/2",
        value: this.getTimeScheduleTimeValue("endAt"),
        hidden: this.object?.unlockType !== UnlockType.time,
      },
    ];

    console.log(
      "[MissioniEsplorative] initial form values:",
      this.regConfig2
        .filter((field) => field.name === "localita" || field.name === "missionCategoryFk")
        .map((field) => ({
          name: field.name,
          value: field.value,
        })),
    );

    this.ready = true;

    setTimeout(() => {
      this.syncRequiredLevelField(this.object?.unlockType ?? UnlockType.immediate);
      this.syncTimeScheduleFields(this.object?.unlockType ?? UnlockType.immediate);
    }, 0);
  }

  initializeEditForm() {
    this.codice = this.route.snapshot.paramMap.get("id") || "";

    this.missioniEsplorativeService
      .read(this.codice)
      .pipe(takeUntil(this.theEnd$))
      .subscribe({
        next: (response: MissionDto & MissioneEsplorativaLocationFields) => {
          console.log("[MissioniEsplorative] read response:", response);
          this.object = _.cloneDeep(response);
          this.localStorageService.setTitolo(
            `Modifica missione esplorativa ${response.name}`,
          );

          let req: customBreadCrumbRequest = {
            oldString: response.id,
            newString: response.name,
          };
          this.localStorageService.setCustomBreadCrumb(req);

          if (this.form2Opts.editOptions) {
            this.form2Opts.editOptions.deleteItem = "" + this.object.id;
            this.form2Opts.editOptions.entityId = this.object.id;
          }

          this.initializeFormFields();
          this.loadIndoorFlag(response.id);
        },
        error: () => {},
      });
  }

  // in modifica: il flag "mappa indoor" e ON se esiste gia una mappa per la missione
  private loadIndoorFlag(missionId: string): void {
    this.mappaIndoorService
      .getAll(missionId)
      .pipe(takeUntil(this.theEnd$))
      .subscribe({
        next: (maps) => {
          this.isIndoor = (maps?.length ?? 0) > 0;
          const field = this.regConfig2.find((f) => f.name === "isIndoor");
          if (field) field.value = this.isIndoor;
          this.myForm?.form?.controls?.["isIndoor"]?.setValue(this.isIndoor, {
            emitEvent: false,
          });
        },
        error: () => {},
      });
  }

  // crea/aggiorna o elimina la mappa indoor in base al flag
  private persistIndoor(missionId: string): Observable<any> {
    if (this.isIndoor) {
      return this.mappaIndoor ? this.mappaIndoor.persist(missionId) : of(null);
    }
    return this.mappaIndoor ? this.mappaIndoor.remove(missionId) : of(void 0);
  }

  submit(event: any) {
    if (!this.validateLevelUnlock(event)) {
      return;
    }

    if (!this.validateTimeUnlock(event)) {
      return;
    }

    // se e una mappa indoor, i dati della planimetria devono essere validi
    if (this.isIndoor && this.mappaIndoor && !this.mappaIndoor.validate()) {
      return;
    }

    if (this.form2Opts.formType == "add") {
      this.add(event);
    } else {
      this.edit(event);
    }
  }

  edit(event: any) {
    console.log("[MissioniEsplorative] edit event:", event);
    console.log("[MissioniEsplorative] edit missionCategoryFk:", event?.missionCategoryFk);
    console.log("[MissioniEsplorative] edit missionRoles raw:", event?.missionRoles);

    const data: UpdateMission & MissioneEsplorativaLocationFields = {
      name: event.name,
      description: event.description,
      shortDescription: event.shortDescription,
      pointsRedeem: event.pointsRedeem,
      hearts: event.hearts,
      adventureFk: (event.adventureFk ?? this.object?.adventureFk ?? null) as any,
      isSecondary: this.object?.isSecondary,
      unlockType: event.unlockType ?? UnlockType.immediate,
      configurationJson: this.buildConfigurationJson(event, true),
      missionCategoryName: this.normalizeSelectValue(event.missionCategoryFk),
      missionRoles: this.buildMissionRoles(event.missionRoles),
      poiFk: this.normalizeSelectValue(event.poiFk),
    };

    console.log("[MissioniEsplorative] edit data:", data);
    console.log(
      "[MissioniEsplorative] update payload missionRoles:",
      data.missionRoles,
    );

    this.missioniEsplorativeService
      .update(this.codice, data)
      .pipe(takeUntil(this.theEnd$))
      .subscribe({
        next: () => {
          this.persistIndoor(this.codice)
            .pipe(takeUntil(this.theEnd$))
            .subscribe({
              next: () => {
                this.toast.success("Modifiche salvate");
                this.initializeForm();
              },
              error: () => {
                this.toast.success("Modifiche salvate");
                this.initializeForm();
              },
            });
        },
        error: () => {},
      });
  }

  add(event: any) {
    console.log("[MissioniEsplorative] add event:", event);
    console.log("[MissioniEsplorative] add missionCategoryFk:", event?.missionCategoryFk);
    console.log("[MissioniEsplorative] add missionRoles raw:", event?.missionRoles);

    const data: CreateMission & MissioneEsplorativaLocationFields = {
      name: event.name,
      description: event.description,
      shortDescription: event.shortDescription,
      pointsRedeem: event.pointsRedeem,
      hearts: event.hearts,
      adventureFk: (event.adventureFk || this.route.snapshot.paramMap.get("adventureId") || null) as any,
      isSecondary: true,
      unlockType: event.unlockType ?? UnlockType.immediate,
      configurationJson: this.buildConfigurationJson(event),
      missionCategoryName: this.normalizeSelectValue(event.missionCategoryFk),
      missionRoles: this.buildMissionRoles(event.missionRoles),
      poiFk: this.normalizeSelectValue(event.poiFk),
    };

    console.log("[MissioniEsplorative] create data:", data);
    console.log(
      "[MissioniEsplorative] create payload missionRoles:",
      data.missionRoles,
    );

    this.missioniEsplorativeService
      .create(data)
      .pipe(takeUntil(this.theEnd$))
      .subscribe({
        next: (response: MissionDto & MissioneEsplorativaLocationFields) => {
          const goToMission = () => {
            this.toast.success("Missione esplorativa creata");
            this.router.navigate(["home/missioni-esplorative", response.id]);
          };

          this.persistIndoor(response.id)
            .pipe(takeUntil(this.theEnd$))
            .subscribe({ next: goToMission, error: goToMission });
        },
        error: () => {},
      });
  }

  goBackToMissioniEsplorative() {
    this.router.navigate(["home/missioni-esplorative"]);
  }

  public handleFormFieldChange(event: any): void {
    const unlockType = event?.values?.unlockType ?? event?.value ?? event?.id;

    if (event?.field === "startDay_all" && event?.value === true) {
      this.selectAllTimeScheduleDays();
    }

    if (event?.field === "unlockType" || event?.values?.unlockType) {
      this.syncRequiredLevelField(unlockType);
      this.syncTimeScheduleFields(unlockType);
    }
  }

  // toggle dello switch "E una mappa indoor?"
  public handleIndoorToggle(event: any): void {
    const value =
      typeof event === "boolean"
        ? event
        : event?.value ?? this.myForm?.form?.controls?.["isIndoor"]?.value ?? false;
    this.isIndoor = !!value;
  }

  public selectAllTimeScheduleDays(): void {
    this.weekDayOptions.forEach((day) => {
      const fieldName = `startDay_${day.value}`;
      const control = this.myForm?.form?.controls?.[fieldName];

      control?.setValue(true);
      control?.markAsDirty();
      control?.updateValueAndValidity({ emitEvent: false });
    });
  }

  // Carica in parallelo POI, avventure e ruoli, poi costruisce il form una sola volta.
  private loadOptions(): void {
    forkJoin({
      pois: this.poiService.getAll().pipe(
        catchError(() => {
          this.toast.error("Errore durante il caricamento dei POI");
          return of([] as Array<{ id: string; name: string }>);
        }),
      ),
      adventures: this.gameService.getAll().pipe(
        catchError(() => {
          this.toast.error("Errore durante il caricamento delle avventure");
          return of([] as Array<{ id: string; name: string }>);
        }),
      ),
      roles: this.http
        .get<RoleApiResponse[]>(`${environment.apiUrl}/users/roles`)
        .pipe(
          catchError(() => {
            this.toast.error("Errore durante il caricamento dei ruoli");
            return of([] as RoleApiResponse[]);
          }),
        ),
    })
      .pipe(takeUntil(this.theEnd$))
      .subscribe(({ pois, adventures, roles }) => {
        this.poiOptions = pois.map((poi) => ({
          id: poi.id,
          codice: poi.name,
        }));

        this.adventureOptions = adventures.map((adventure) => ({
          id: adventure.id,
          codice: adventure.name,
        }));

        const allowedRoles = (roles ?? []).filter(
          (role) => role.name === "User" || role.name === "Guest",
        );
        this.defaultRoleId =
          allowedRoles.find((role) => role.name === "User")?.id ?? null;
        this.roleOptions = allowedRoles.map((role) => ({
          id: role.id,
          codice: role.name,
        }));

        // tutte le opzioni sono pronte: costruzione del form una sola volta
        this.initializeForm();
      });
  }

  private normalizeSelectValue(value: string | number | null | undefined): string | null {
    const normalized = value?.toString().trim();
    return normalized ? normalized.toUpperCase() : null;
  }

  private normalizeRoleSelection(
    value: string | string[] | null | undefined,
  ): string[] | null {
    if (Array.isArray(value)) {
      const normalized = value
        .map((item) => item?.toString().trim())
        .filter((item): item is string => !!item);

      return normalized.length > 0 ? normalized : null;
    }

    const normalized = value?.toString().trim();
    return normalized ? [normalized] : null;
  }

  private buildMissionRoles(
    value: string | string[] | null | undefined,
  ): MissionRole[] {
    const selectedIds = this.normalizeRoleSelection(value) ?? [];
    const selectedRoles = this.roleOptions.filter((role) =>
      selectedIds.includes(role.id),
    );

    if (selectedRoles.length > 0) {
      return selectedRoles.map((role) => ({
        roleId: role.id,
        roleName: role.codice,
      }));
    }

    const defaultRole = this.roleOptions.find(
      (role) => role.id === this.defaultRoleId,
    );

    return defaultRole
      ? [
          {
            roleId: defaultRole.id,
            roleName: defaultRole.codice,
          },
        ]
      : [];
  }

  private getSelectedRoleValue(): string[] | null {
    const selectedRoles = this.object?.missionRoles
      ?.map((role) => role.roleId?.trim())
      .filter((roleId): roleId is string => !!roleId);

    if (selectedRoles && selectedRoles.length > 0) {
      return selectedRoles;
    }

    return this.defaultRoleId ? [this.defaultRoleId] : null;
  }

  private buildDayCheckboxFields(prefix: string, label: string): FieldConfig[] {
    const isTimeUnlock = this.object?.unlockType === UnlockType.time;

    return [
      {
        type: FieldTypeEnum.title,
        name: `${prefix}Title`,
        label,
        width: "w-full",
        cssClass: "text-base",
        hidden: !isTimeUnlock,
      },
      {
        type: FieldTypeEnum.checkbox,
        name: `${prefix}_all`,
        label: "Tutti",
        width: "w-1/12",
        cssClass: "time-day-checkbox time-day-checkbox-all",
        value: this.areAllTimeScheduleDaysSelected("startDays"),
        hidden: !isTimeUnlock,
      },
      ...this.weekDayOptions.map((day) => ({
        type: FieldTypeEnum.checkbox,
        name: `${prefix}_${day.value}`,
        label: day.label,
        width: "w-1/12" as FieldConfig["width"],
        cssClass: "time-day-checkbox",
        value: this.isTimeScheduleDaySelected(
          prefix === "startDay" ? "startDays" : "endDays",
          day.value,
        ),
        hidden: !isTimeUnlock,
      })),
    ];
  }

  private syncRequiredLevelField(unlockType: UnlockType | string | null | undefined): void {
    const isLevelUnlock = unlockType === UnlockType.level;
    const fieldIndex = this.regConfig2.findIndex((field) => field.name === "requiredLevel");

    if (fieldIndex < 0) {
      return;
    }

    const nextHidden = !isLevelUnlock;
    // Riassegna regConfig2 (=> re-render del form) solo se lo stato cambia davvero.
    // Al primo caricamento il campo è già costruito con l'hidden corretto: evitare la
    // riassegnazione no-op impedisce un render extra che lascerebbe nodi DOM fantasma.
    if (this.regConfig2[fieldIndex].hidden !== nextHidden) {
      this.regConfig2[fieldIndex].hidden = nextHidden;
      this.regConfig2 = [...this.regConfig2];

      if (this.myForm?.parsedFields) {
        this.myForm.parsedFields = _.cloneDeep(this.regConfig2);
      }
    }

    const control = this.myForm?.form?.controls?.["requiredLevel"];
    if (!control) {
      return;
    }

    if (isLevelUnlock) {
      control.setValidators([Validators.required, Validators.min(1)]);
    } else {
      control.clearValidators();
      control.setErrors(null);
    }

    control.updateValueAndValidity({ emitEvent: false });
  }

  private syncTimeScheduleFields(unlockType: UnlockType | string | null | undefined): void {
    const isTimeUnlock = unlockType === UnlockType.time;
    const nextHidden = !isTimeUnlock;

    const isTimeScheduleField = (name: string) =>
      name === "timeScheduleDivider" ||
      this.timeScheduleFieldNames.includes(name) ||
      this.isTimeScheduleDayField(name);

    // Riassegna (=> re-render) solo se almeno un campo cambia visibilità: evita il
    // render no-op al primo caricamento (che lascia nodi DOM fantasma del form).
    const changed = this.regConfig2.some(
      (field) => isTimeScheduleField(field.name) && field.hidden !== nextHidden,
    );

    if (changed) {
      this.regConfig2 = this.regConfig2.map((field) =>
        isTimeScheduleField(field.name)
          ? { ...field, hidden: nextHidden }
          : field,
      );

      if (this.myForm?.parsedFields) {
        this.myForm.parsedFields = _.cloneDeep(this.regConfig2);
      }
    }

    this.timeScheduleFieldNames.forEach((fieldName) => {
      const control = this.myForm?.form?.controls?.[fieldName];
      if (!control) {
        return;
      }

      if (isTimeUnlock) {
        control.setValidators([Validators.required]);
      } else {
        control.clearValidators();
        control.setErrors(null);
      }

      control.updateValueAndValidity({ emitEvent: false });
    });
  }

  private validateLevelUnlock(event: any): boolean {
    if (event.unlockType !== UnlockType.level) {
      return true;
    }

    const requiredLevel = Number(event.requiredLevel);
    if (Number.isInteger(requiredLevel) && requiredLevel > 0) {
      return true;
    }

    const control = this.myForm?.form?.controls?.["requiredLevel"];
    control?.setErrors({ min: true });
    control?.markAsTouched();
    this.toast.error("Inserisci il livello richiesto di sblocco");
    return false;
  }

  private validateTimeUnlock(event: any): boolean {
    if (event.unlockType !== UnlockType.time) {
      return true;
    }

    const hasStartDay = this.getSelectedTimeScheduleDays(event, "startDay").length > 0;

    if (!hasStartDay) {
      this.toast.error("Seleziona almeno un giorno di inizio");
      return false;
    }

    const missingField = this.timeScheduleFieldNames.find((fieldName) => {
      const value = event[fieldName]?.toString().trim();
      return !value;
    });

    if (!missingField) {
      return true;
    }

    const control = this.myForm?.form?.controls?.[missingField];
    control?.setErrors({ required: true });
    control?.markAsTouched();
    this.toast.error("Completa la schedulazione dello sblocco a tempo");
    return false;
  }

  private buildConfigurationJson(
    event: any,
    preserveExisting: boolean = false,
  ): LevelUnlockConfig | Record<string, any> {
    if (event.unlockType === UnlockType.level) {
      return {
        RequiredLevel: Number(event.requiredLevel),
      };
    }

    if (event.unlockType === UnlockType.time) {
      const selectedStartDays = this.getSelectedTimeScheduleDays(event, "startDay");

      return {
        startDays: this.normalizeDayValue(selectedStartDays),
        endDays: this.getLastSelectedTimeScheduleDay(selectedStartDays),
        startAt: this.normalizeTimeValue(event.startAt),
        endAt: this.normalizeTimeValue(event.endAt),
      };
    }

    return preserveExisting
      ? this.getMissionConfiguration() ?? {}
      : {};
  }

  private getRequiredLevelValue(): number | null {
    const requiredLevel = this.getConfigurationValue("RequiredLevel") ?? this.getConfigurationValue("requiredLevel");
    const parsedRequiredLevel = Number(requiredLevel);

    return Number.isFinite(parsedRequiredLevel) && parsedRequiredLevel > 0
      ? parsedRequiredLevel
      : null;
  }

  private getTimeScheduleValue(fieldName: string): string[] {
    const value = this.getConfigurationValue(fieldName);

    if (Array.isArray(value)) {
      return value
        .map((item) => item?.toString().trim().toLowerCase())
        .filter((item): item is string => !!item);
    }

    const normalized = value?.toString().trim().toLowerCase();
    if (!normalized) {
      return ["everydays"];
    }

    if (normalized === "everydays") {
      return this.weekDayOptions.map((day) => day.value);
    }

    return normalized
      .split(",")
      .map((item: string) => item.trim())
      .filter((item: string) => !!item);
  }

  private getTimeScheduleTimeValue(fieldName: string): string | null {
    const value = this.getConfigurationValue(fieldName)?.toString().trim();

    if (!value) {
      return null;
    }

    return value.slice(0, 5);
  }

  private isTimeScheduleDaySelected(fieldName: string, dayValue: string): boolean {
    return this.getTimeScheduleValue(fieldName).includes(dayValue);
  }

  private areAllTimeScheduleDaysSelected(fieldName: string): boolean {
    const selectedDays = this.getTimeScheduleValue(fieldName);

    return this.weekDayOptions.every((day) => selectedDays.includes(day.value));
  }

  private isTimeScheduleDayField(fieldName: string): boolean {
    return (
      this.timeScheduleDayPrefixes.some((prefix) =>
        fieldName.startsWith(`${prefix}_`) || fieldName === `${prefix}Title`,
      )
    );
  }

  private getSelectedTimeScheduleDays(event: any, prefix: string): string[] {
    return this.weekDayOptions
      .filter((day) => event[`${prefix}_${day.value}`] === true)
      .map((day) => day.value);
  }

  private getLastSelectedTimeScheduleDay(selectedDays: string[]): string {
    if (selectedDays.length === this.weekDayOptions.length) {
      return "everydays";
    }

    return (
      [...this.endDayOrder]
        .reverse()
        .find((day) => selectedDays.includes(day)) ?? selectedDays[selectedDays.length - 1]
    );
  }

  private normalizeDayValue(value: string[]): string {
    if (value.length === this.weekDayOptions.length) {
      return "everydays";
    }

    return value.map((day) => day.toLowerCase()).join(",");
  }

  private normalizeTimeValue(value: string | null | undefined): string {
    const normalized = value?.toString().trim() || "";

    if (!normalized) {
      return "";
    }

    return normalized.length === 5 ? `${normalized}:00` : normalized;
  }

  private getConfigurationValue(fieldName: string): any {
    const config = this.getMissionConfiguration();

    if (!config) {
      return undefined;
    }

    const pascalCaseFieldName = `${fieldName.charAt(0).toUpperCase()}${fieldName.slice(1)}`;
    return (
      config[fieldName] ??
      config[pascalCaseFieldName] ??
      config[fieldName.toLowerCase()]
    );
  }

  private getMissionConfiguration(): Record<string, any> | null {
    const objectWithPossibleAliases = this.object as
      | (MissionDto & MissioneEsplorativaLocationFields & Record<string, any>)
      | undefined;

    return (
      this.parseConfigurationJson(objectWithPossibleAliases?.configurationJson) ??
      this.parseConfigurationJson(objectWithPossibleAliases?.["configurationJsdon"]) ??
      this.parseConfigurationJson(objectWithPossibleAliases?.["configurationJSON"]) ??
      this.parseConfigurationJson(objectWithPossibleAliases?.["configuration"])
    );
  }

  private parseConfigurationJson(
    configurationJson: MissionDto["configurationJson"] | Record<string, any> | undefined,
  ): Record<string, any> | null {
    if (!configurationJson) {
      return null;
    }

    if (typeof configurationJson !== "string") {
      return configurationJson;
    }

    try {
      const parsedConfiguration = JSON.parse(configurationJson);

      if (typeof parsedConfiguration === "string") {
        return this.parseConfigurationJson(parsedConfiguration);
      }

      return parsedConfiguration;
    } catch {
      return null;
    }
  }

  downloadQR(index: number, code: string) {
    this.loadingQrCode = true;
    const host = this.qrComps.toArray()[index]?.nativeElement as HTMLElement | undefined;
    if (!host) { this.loadingQrCode = false; return; }

    const srcCanvas = host.querySelector("canvas") as HTMLCanvasElement | null;
    if (!srcCanvas) { this.loadingQrCode = false; return; }

    const dpr = window.devicePixelRatio || 1;
    const qrW = srcCanvas.width;
    const qrH = srcCanvas.height;
    const bgColor = "#1e1e1e";
    const padding = 16;
    let fontSize = 18;
    const fontFamily = "Inter, Arial, sans-serif";
    const text = code || "";

    const measureCtx = document.createElement("canvas").getContext("2d")!;
    const fitText = (targetWidth: number) => {
      let fs = fontSize;
      while (fs > 10) {
        measureCtx.font = `${fs * dpr}px ${fontFamily}`;
        if (measureCtx.measureText(text).width <= (targetWidth - 24) * dpr) break;
        fs -= 1;
      }
      return fs;
    };
    fontSize = fitText(qrW / dpr);

    const textLineHeight = Math.round(fontSize * 1.4 * dpr);
    const padPx = Math.round(padding * dpr);

    const outCanvas = document.createElement("canvas");
    outCanvas.width = qrW;
    outCanvas.height = qrH + padPx + textLineHeight + padPx;

    const ctx = outCanvas.getContext("2d")!;
    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, outCanvas.width, outCanvas.height);
    ctx.drawImage(srcCanvas, 0, 0);
    ctx.fillStyle = "#ffffff";
    ctx.font = `${fontSize * dpr}px ${fontFamily}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(text, outCanvas.width / 2, qrH + padPx + Math.floor(textLineHeight / 2));

    const a = document.createElement("a");
    a.href = outCanvas.toDataURL("image/png");
    a.download = `qr_${this.sanitizeFilename(code)}.png`;
    a.click();

    this.loadingQrCode = false;
  }

  sanitizeFilename(name: string) {
    return (name || "code").replace(/[<>:"/\\|?*\x00-\x1F]/g, "_").slice(0, 100);
  }
}
